import { byId, clear, el, icon, setBusy, setHidden, setText } from "../dom";
import {
  channelLabel,
  formatCount,
  formatDistanceKm,
  formatEtaDetailed,
  formatEtaMinutes,
  leadLabel,
} from "../format";
import type { CreateHouseholdRequest, EtaResponse, EtaStatus, Household, HouseholdChannel } from "../types";
import { emptyState, errorState, skeletonRows } from "./states";
import type { LoadStatus } from "./operation";

export type EtaEntry =
  | { status: "pending" }
  | { status: "done"; result: EtaResponse }
  | { status: "error"; message: string };

export interface HouseholdsListState {
  status: LoadStatus;
  households: readonly Household[];
  total: number;
  offset: number;
  limit: number;
  query: string;
  error: string | null;
}

export interface HouseholdsHandlers {
  onReload(): void;
  onSearch(query: string): void;
  onPage(offset: number): void;
  onEta(household: Household): void;
  onEdit(household: Household): void;
  onCancelEdit(): void;
  onDelete(household: Household): void;
  onViewAlerts(household: Household): void;
  onPickOnMap(): void;
  onGeolocate(): void;
  onClearLocation(): void;
  onImport(text: string): void;
  onExport(): void;
  onSubmit(payload: CreateHouseholdRequest): void;
}

export interface HouseholdsController {
  setList(state: HouseholdsListState): void;
  setEta(householdId: string, entry: EtaEntry): void;
  setLocation(lat: number | null, lng: number | null): void;
  getLocation(): { lat: number; lng: number } | null;
  setPicking(active: boolean): void;
  setSubmitting(busy: boolean): void;
  setLocationBusy(busy: boolean): void;
  setDeleting(householdId: string | null): void;
  setFormError(message: string | null, field?: "name" | "location"): void;
  startEdit(household: Household): void;
  stopEdit(): void;
  resetForm(): void;
  setSearchQuery(value: string): void;
}

const CHANNEL_VALUES: readonly HouseholdChannel[] = ["push", "whatsapp", "call"];
const SEARCH_DEBOUNCE_MS = 300;

function normalize(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function etaBadge(status: EtaStatus): { label: string; className: string } {
  switch (status) {
    case "approaching":
      return { label: "Se acerca", className: "chip chip--ok" };
    case "far":
      return { label: "Todavía lejos", className: "chip chip--warn" };
    case "no_active_truck":
      return { label: "Sin camión activo", className: "chip chip--muted" };
  }
}

export function initHouseholds(handlers: HouseholdsHandlers): HouseholdsController {
  const form = byId<HTMLFormElement>("household-form");
  const formMode = byId<HTMLParagraphElement>("form-mode");
  const formModeText = byId<HTMLSpanElement>("form-mode-text");
  const nameInput = byId<HTMLInputElement>("hh-name");
  const phoneInput = byId<HTMLInputElement>("hh-phone");
  const latInput = byId<HTMLInputElement>("hh-lat");
  const lngInput = byId<HTMLInputElement>("hh-lng");
  const leadSelect = byId<HTMLSelectElement>("hh-lead");
  const locationChip = byId<HTMLSpanElement>("hh-location-chip");
  const locationHelp = byId<HTMLParagraphElement>("hh-location-help");
  const locationBox = form.querySelector<HTMLElement>(".location");
  const pickButton = byId<HTMLButtonElement>("hh-pick");
  const geolocateButton = byId<HTMLButtonElement>("hh-geolocate");
  const clearButton = byId<HTMLButtonElement>("hh-clear");
  const submitButton = byId<HTMLButtonElement>("hh-submit");
  const cancelEditButton = byId<HTMLButtonElement>("hh-cancel-edit");
  const formError = byId<HTMLParagraphElement>("form-error");
  const reloadButton = byId<HTMLButtonElement>("reload-households");
  const listElement = byId<HTMLUListElement>("households-list");
  const countLabel = byId<HTMLParagraphElement>("households-count");
  const searchInput = byId<HTMLInputElement>("hh-search");
  const foot = byId<HTMLDivElement>("households-foot");
  const rangeLabel = byId<HTMLParagraphElement>("households-range");
  const prevButton = byId<HTMLButtonElement>("households-prev");
  const nextButton = byId<HTMLButtonElement>("households-next");
  const channelInputs: Record<HouseholdChannel, HTMLInputElement> = {
    push: byId<HTMLInputElement>("hh-channel-push"),
    whatsapp: byId<HTMLInputElement>("hh-channel-whatsapp"),
    call: byId<HTMLInputElement>("hh-channel-call"),
  };

  let households: readonly Household[] = [];
  let listStatus: LoadStatus = "idle";
  let listError: string | null = null;
  let listTotal = 0;
  let listOffset = 0;
  let listLimit = 8;
  let listQuery = "";
  let editingId: string | null = null;
  let deletingId: string | null = null;
  const eta = new Map<string, EtaEntry>();
  let location: { lat: number; lng: number } | null = null;

  function setLocationChip(): void {
    if (location === null) {
      locationChip.className = "chip chip--muted";
      clear(locationChip);
      locationChip.append(icon("i-pin"), el("span", { text: "Sin ubicación" }));
      latInput.value = "";
      lngInput.value = "";
      locationBox?.classList.remove("location--active");
      setHidden(clearButton, true);
      setText(
        locationHelp,
        "Marca en el mapa dónde queda el hogar; es lo que usa el cálculo del aviso.",
      );
      return;
    }
    locationChip.className = "chip chip--accent";
    clear(locationChip);
    locationChip.append(
      icon("i-pin"),
      el("span", { text: `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}` }),
    );
    latInput.value = location.lat.toFixed(6);
    lngInput.value = location.lng.toFixed(6);
    locationBox?.classList.add("location--active");
    setHidden(clearButton, false);
    setText(locationHelp, "Ubicación lista. Asígnale un nombre al hogar y guárdalo.");
  }

  function renderFoot(): void {
    const ready = listStatus === "ready";
    setHidden(foot, !(ready && listTotal > 0));
    if (!ready || listTotal === 0) return;

    const from = listOffset + 1;
    const to = listOffset + households.length;
    setText(
      rangeLabel,
      `Mostrando ${formatCount(from)}–${formatCount(to)} de ${formatCount(listTotal)} ${
        listTotal === 1 ? "hogar" : "hogares"
      }`,
    );
    prevButton.disabled = listOffset <= 0;
    nextButton.disabled = listOffset + listLimit >= listTotal;
  }

  function renderList(): void {
    const scrollTop = listElement.scrollTop;
    clear(listElement);

    if (listStatus === "idle" || listStatus === "loading") {
      setText(countLabel, "Cargando hogares…");
      listElement.append(skeletonRows(3));
      renderFoot();
      return;
    }

    if (listStatus === "error") {
      setText(countLabel, "Sin datos");
      listElement.append(
        errorState({
          title: "No pudimos cargar los hogares",
          message: listError ?? "Revisa la conexión e intenta de nuevo.",
          onRetry: handlers.onReload,
        }),
      );
      renderFoot();
      return;
    }

    const term = normalize(listQuery);
    if (households.length === 0 && listTotal === 0) {
      setText(countLabel, term === "" ? "Todavía no hay hogares" : "Sin coincidencias");
      listElement.append(
        term === ""
          ? emptyState({
              iconName: "i-home",
              title: "Aún no hay hogares",
              message: "Crea el primero marcando su ubicación en el mapa: los avisos llegan solos.",
              actionLabel: "Crear el primer hogar",
              onAction: focusForm,
            })
          : emptyState({
              iconName: "i-search",
              title: "Sin resultados",
              message: `Ningún hogar coincide con «${listQuery.trim()}».`,
              actionLabel: "Limpiar búsqueda",
              onAction: () => {
                searchInput.value = "";
                handlers.onSearch("");
              },
            }),
      );
      renderFoot();
      return;
    }

    setText(
      countLabel,
      term === ""
        ? `${formatCount(listTotal)} ${listTotal === 1 ? "hogar" : "hogares"}`
        : `${formatCount(listTotal)} ${listTotal === 1 ? "coincidencia" : "coincidencias"}`,
    );

    for (const household of households) listElement.append(buildRow(household));
    listElement.scrollTop = scrollTop;
    renderFoot();
  }

  function focusForm(): void {
    form.scrollIntoView({ behavior: "smooth", block: "start" });
    nameInput.focus();
  }

  function buildRow(household: Household): HTMLLIElement {
    const item = el("li", { className: "hh" });
    if (editingId === household.id) item.classList.add("hh--editing");
    if (deletingId === household.id) item.classList.add("hh--busy");

    const main = el("div", { className: "hh__main" });
    const phone = household.phone?.trim() ?? "";
    main.append(
      el("span", { className: "hh__name", text: household.name, title: household.name }),
      el("span", {
        className: "hh__meta",
        text: `${phone === "" ? "Sin teléfono" : phone} · ${leadLabel(household.lead_minutes)}`,
      }),
    );

    const side = el("div", { className: "hh__side" });
    const chips = el("div", { className: "hh__chips" });
    for (const channel of household.channels) {
      chips.append(el("span", { className: "chip", text: channelLabel(channel) }));
    }
    side.append(chips);

    const actions = el("div", { className: "hh__actions" });
    actions.append(
      actionButton("i-truck", etaLabel(eta.get(household.id)), "Ver ETA", () => handlers.onEta(household)),
      actionButton("i-bell", "Alertas", `Ver alertas de «${household.name}»`, () =>
        handlers.onViewAlerts(household),
      ),
      actionButton("i-edit", "Editar", `Editar «${household.name}»`, () => handlers.onEdit(household)),
    );
    const remove = el("button", { className: "btn btn--danger-quiet btn--sm btn--icon", title: "Borrar hogar" });
    remove.type = "button";
    remove.setAttribute("aria-label", `Borrar «${household.name}»`);
    remove.append(icon("i-trash"));
    remove.addEventListener("click", () => handlers.onDelete(household));
    actions.append(remove);

    item.append(main, side, actions);

    const entry = eta.get(household.id);
    if (entry !== undefined) item.append(buildResult(entry, household));
    return item;
  }

  function actionButton(iconName: string, label: string, title: string, onClick: () => void): HTMLButtonElement {
    const button = el("button", { className: "btn btn--ghost btn--sm", title });
    button.type = "button";
    button.append(icon(iconName), el("span", { text: label }));
    button.addEventListener("click", onClick);
    return button;
  }

  function etaLabel(entry: EtaEntry | undefined): string {
    if (entry === undefined) return "Ver ETA";
    if (entry.status === "done" && entry.result.eta_seconds !== undefined) {
      return `Ver ETA · ${formatEtaMinutes(entry.result.eta_seconds)}`;
    }
    return "Ver ETA";
  }

  function buildResult(entry: EtaEntry, household: Household): HTMLElement {
    const result = el("div", { className: "hh__result" });

    if (entry.status === "pending") {
      result.append(el("div", { className: "skeleton skeleton--line", text: "" }));
      return result;
    }

    if (entry.status === "error") {
      const error = el("div", { className: "hh__error" });
      error.append(icon("i-alert"), el("span", { text: entry.message }));
      const retry = el("button", { className: "btn btn--ghost btn--sm", text: "Reintentar" });
      retry.type = "button";
      retry.addEventListener("click", () => handlers.onEta(household));
      error.append(retry);
      result.append(error);
      return result;
    }

    const head = el("div", { className: "hh__result-head" });
    const badge = etaBadge(entry.result.status);
    head.append(el("span", { className: badge.className, text: badge.label }));
    if (entry.result.notified) head.append(el("span", { className: "chip chip--accent", text: "Alerta enviada" }));

    const details: string[] = [];
    if (entry.result.eta_seconds !== undefined) details.push(`ETA ${formatEtaDetailed(entry.result.eta_seconds)}`);
    if (entry.result.distance_m !== undefined) details.push(`a ${formatDistanceKm(entry.result.distance_m)}`);
    if (entry.result.vehicle_label !== undefined) {
      const route = entry.result.route_name ?? "";
      details.push(route === "" ? entry.result.vehicle_label : `${entry.result.vehicle_label} · ${route}`);
    }

    result.append(head, el("p", { className: "hh__message", text: entry.result.message }));
    if (details.length > 0) result.append(el("p", { className: "hh__details", text: details.join(" · ") }));
    return result;
  }

  function readLocationFromInputs(): void {
    const lat = Number(latInput.value);
    const lng = Number(lngInput.value);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0 || lng === 0) return;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return;
    location = { lat, lng };
    setLocationChip();
  }

  function applyFormError(message: string | null, field?: "name" | "location"): void {
    setHidden(formError, message === null);
    if (message !== null) {
      clear(formError);
      formError.append(icon("i-alert"), el("span", { text: message }));
    }
    nameInput.setAttribute("aria-invalid", field === "name" && message !== null ? "true" : "false");
    if (field === "location" && message !== null) locationBox?.classList.add("location--invalid");
    else locationBox?.classList.remove("location--invalid");
  }

  function applyPicking(active: boolean): void {
    pickButton.setAttribute("aria-pressed", active ? "true" : "false");
    pickButton.classList.toggle("btn--ghost", !active);
    pickButton.classList.toggle("btn--primary", active);
    setHidden(clearButton, active || location === null);
    if (active) setText(locationHelp, "Toca el mapa para dejar el punto del hogar.");
    else setLocationChip();
  }

  function applyEditing(household: Household | null): void {
    editingId = household?.id ?? null;
    setHidden(formMode, household === null);
    if (household !== null) setText(formModeText, `Editando «${household.name}»`);
    setText(submitButton, household === null ? "Crear hogar" : "Guardar cambios");
    setHidden(cancelEditButton, household === null);
  }

  function ensureLeadOption(minutes: number): void {
    if ([...leadSelect.options].some((option) => Number(option.value) === minutes)) return;
    const option = el("option", { text: `${minutes} minutos` });
    option.value = String(minutes);
    leadSelect.append(option);
  }

  function submit(): void {
    const name = nameInput.value.trim();
    if (name === "") {
      applyFormError("Ponle un nombre al hogar para poder identificarlo.", "name");
      nameInput.focus();
      return;
    }
    if (location === null) {
      applyFormError("Marca la ubicación del hogar en el mapa o escribe sus coordenadas.", "location");
      return;
    }
    const leadMinutes = Number(leadSelect.value);
    if (!Number.isInteger(leadMinutes) || leadMinutes < 5 || leadMinutes > 120) {
      applyFormError("La anticipación debe estar entre 5 y 120 minutos.");
      return;
    }
    const channels = CHANNEL_VALUES.filter((channel) => channelInputs[channel].checked);
    if (channels.length === 0) {
      applyFormError("Elige al menos un canal de aviso: App, WhatsApp o llamada.");
      return;
    }
    const phone = phoneInput.value.trim();
    const payload: CreateHouseholdRequest = {
      name,
      lat: location.lat,
      lng: location.lng,
      lead_minutes: leadMinutes,
      channels,
    };
    // Al editar, un teléfono vacío debe viajar como "" para poder borrarlo.
    if (phone !== "" || editingId !== null) payload.phone = phone;
    applyFormError(null);
    handlers.onSubmit(payload);
  }

  function resetFormFields(): void {
    nameInput.value = "";
    phoneInput.value = "";
    leadSelect.value = "10";
    channelInputs.push.checked = true;
    channelInputs.whatsapp.checked = false;
    channelInputs.call.checked = false;
    location = null;
    applyPicking(false);
    setLocationChip();
    applyFormError(null);
    nameInput.focus();
  }

  let searchTimer: number | null = null;
  function scheduleSearch(value: string): void {
    if (searchTimer !== null) window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
      searchTimer = null;
      handlers.onSearch(value.trim());
    }, SEARCH_DEBOUNCE_MS);
  }

  reloadButton.addEventListener("click", handlers.onReload);
  pickButton.addEventListener("click", handlers.onPickOnMap);
  geolocateButton.addEventListener("click", handlers.onGeolocate);
  clearButton.addEventListener("click", handlers.onClearLocation);
  cancelEditButton.addEventListener("click", handlers.onCancelEdit);

  const importButton = byId<HTMLButtonElement>("import-households");
  const exportButton = byId<HTMLButtonElement>("export-households");
  const importFile = byId<HTMLInputElement>("import-households-file");
  importButton.addEventListener("click", () => importFile.click());
  exportButton.addEventListener("click", handlers.onExport);
  importFile.addEventListener("change", () => {
    const file = importFile.files?.[0];
    if (file === undefined) return;
    void file.text().then((text) => {
      importFile.value = "";
      handlers.onImport(text);
    });
  });
  prevButton.addEventListener("click", () => handlers.onPage(Math.max(0, listOffset - listLimit)));
  nextButton.addEventListener("click", () => handlers.onPage(listOffset + listLimit));
  searchInput.addEventListener("input", () => scheduleSearch(searchInput.value));
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    submit();
  });
  for (const input of [latInput, lngInput]) {
    input.addEventListener("input", readLocationFromInputs);
  }

  setLocationChip();
  renderList();

  return {
    setList(state) {
      households = state.households;
      listStatus = state.status;
      listError = state.error;
      listTotal = state.total;
      listOffset = state.offset;
      listLimit = state.limit;
      listQuery = state.query;
      renderList();
    },

    setEta(householdId, entry) {
      eta.set(householdId, entry);
      renderList();
    },

    setLocation(lat, lng) {
      location = lat === null || lng === null ? null : { lat, lng };
      setLocationChip();
    },

    getLocation() {
      return location;
    },

    setPicking(active) {
      applyPicking(active);
    },

    setSubmitting(busy) {
      setBusy(submitButton, busy);
    },

    setLocationBusy(busy) {
      setBusy(geolocateButton, busy);
      if (busy) setText(geolocateButton, "Buscando…");
      else {
        clear(geolocateButton);
        geolocateButton.append(icon("i-crosshair"), el("span", { text: "Usar mi ubicación" }));
      }
    },

    setDeleting(householdId) {
      deletingId = householdId;
      renderList();
    },

    setFormError(message, field) {
      applyFormError(message, field);
    },

    startEdit(household) {
      applyEditing(household);
      nameInput.value = household.name;
      phoneInput.value = household.phone ?? "";
      ensureLeadOption(household.lead_minutes);
      leadSelect.value = String(household.lead_minutes);
      for (const channel of CHANNEL_VALUES) channelInputs[channel].checked = household.channels.includes(channel);
      location = { lat: household.lat, lng: household.lng };
      setLocationChip();
      applyFormError(null);
      form.scrollIntoView({ behavior: "smooth", block: "start" });
      nameInput.focus();
    },

    stopEdit() {
      applyEditing(null);
      resetFormFields();
    },

    resetForm() {
      resetFormFields();
    },

    setSearchQuery(value) {
      searchInput.value = value;
    },
  };
}
